import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Eye, Mail, RotateCcw, Save, Undo2 } from 'lucide-react';
import * as Api from './ProgramAdminApi';
import PlaceholderEditor, { KIND_STYLE } from './PlaceholderEditor';
import { Actions, Button, Card, CardTitle, Empty, Message, Select } from './ui';

// The invite-reminder template editor, moved into the admin Program screen's People area at the
// switch (plan §7.7 item 3: the People area owns invitations and reminders). It is the old Speaker
// Management editor's TemplateTab on the routes the switch kept alive - GET | PUT | DELETE
// /api/cms/invite-reminders/template, controller.InviteReminderTemplateController - while the old
// screen's pending list and manual send are switched off: the People rows invite, remind, stop and
// resume on the new model (invitation.ReminderController).
//
// One template serves every event and every speaker, so the template routes carry no event id, and
// the copy says so. Two things are new against the old editor:
//   * the live preview is invitation.ReminderController's POST .../reminders/preview, the same route
//     the invitation and reminder dialogs use, so it needs a person - it fills the placeholders with
//     the values that person's own reminder would carry. The select lists the people of this event
//     with an open invitation; when nobody is waiting, the editor says so and saves without a preview
//     (the template is not per event, so an event with nothing pending must not freeze the edit);
//   * the answer is a RenderedMail, whose `templateProblems` is what blocks the save ("this can't be
//     saved yet"), replacing the old preview route's `problems` field.
//
// It loads only when the admin opens it: the People area is about people, and the template is edited
// rarely, so the two reads (the template, the people waiting) stay off the tab's own path.
const backendMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (data && typeof data.message === 'string' && data.message) return data.message;
  return fallback;
};

// Placeholder copy is translated here; the backend's English text is the fallback.
const phText = (intl, p, field) => intl.formatMessage({ id: `inviteReminders.ph.${p.key}.${field}`, defaultMessage: p[field] });

const Legend = () => (
  <div style={{ display: 'flex', gap: '.9rem', flexWrap: 'wrap', fontSize: '.74rem', color: '#475569' }}>
    {[
      ['TEXT', <FormattedMessage key="t" id="inviteReminders.legend.text" defaultMessage="value from the database" />],
      ['BLOCK', <FormattedMessage key="b" id="inviteReminders.legend.block" defaultMessage="ready-made block (buttons, images, details)" />],
      ['UNKNOWN', <FormattedMessage key="u" id="inviteReminders.legend.unknown" defaultMessage="unknown placeholder" />],
    ].map(([kind, label]) => (
      <span key={kind} style={{ display: 'inline-flex', alignItems: 'center', gap: '.35rem' }}>
        <span style={{ width: 14, height: 10, borderRadius: 2, display: 'inline-block', ...KIND_STYLE[kind] }} />{label}
      </span>
    ))}
  </div>
);

const ReminderTemplateCard = ({ eventId }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);

  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [waiting, setWaiting] = useState([]);
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [sampleFor, setSampleFor] = useState('');
  const [highlight, setHighlight] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const subjectRef = useRef(null);
  const htmlRef = useRef(null);
  const lastFocused = useRef('html');
  const requestSeq = useRef(0);

  const apply = useCallback((data) => { setSaved(data); setSubject(data.subject); setHtml(data.html); }, []);

  // The template, and who a preview can fill it in for. Both are read once, when the editor opens
  // (and again if the admin switches event after that).
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    Api.fetchReminderTemplate()
      .then((data) => { if (!cancelled) apply(data); })
      .catch((err) => {
        if (!cancelled) setLoadError(backendMessage(err, t('inviteReminders.template.loadFailed', 'Could not load the template.')));
      });
    return () => { cancelled = true; };
  }, [open, apply]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || eventId == null) return undefined;
    let cancelled = false;
    Api.fetchReminders(eventId)
      .then((data) => {
        if (cancelled) return;
        const people = (data?.people || []).filter((p) => p.openInvitations > 0);
        setWaiting(people);
        // A person the preview can use, so the editor shows a real email without a click.
        setSampleFor((current) => (people.some((p) => String(p.personId) === current)
          ? current : (people[0] ? String(people[0].personId) : '')));
      })
      .catch(() => { if (!cancelled) setWaiting([]); });
    return () => { cancelled = true; };
  }, [open, eventId]);

  const placeholders = useMemo(() => saved?.placeholders || [], [saved]);
  const kindOf = useCallback((key) => placeholders.find((p) => p.key === key)?.kind, [placeholders]);
  const groups = useMemo(() => placeholders.reduce((acc, p) => { (acc[p.group] = acc[p.group] || []).push(p); return acc; }, {}), [placeholders]);

  // The live preview: the unsaved text rendered by the same code as the real send. `of` records which
  // text it was made from, so Save can insist the admin is looking at a preview of what they save.
  const contentKey = JSON.stringify([subject, html]);
  useEffect(() => {
    if (!open || !saved || !sampleFor) return undefined;
    const seq = ++requestSeq.current;
    setPreviewing(true);
    const timer = setTimeout(async () => {
      try {
        const data = await Api.previewReminder(eventId, {
          personId: Number(sampleFor), subject, html, highlight,
        });
        if (seq === requestSeq.current) { setPreview({ ...data, of: contentKey }); setPreviewError(''); }
      } catch (err) {
        if (seq === requestSeq.current) setPreviewError(backendMessage(err, t('inviteReminders.preview.error', 'Could not prepare the preview.')));
      } finally {
        if (seq === requestSeq.current) setPreviewing(false);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [open, saved, sampleFor, highlight, contentKey, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <CardTitle style={{ margin: 0 }}>
              <Mail size={17} style={{ verticalAlign: '-3px', marginRight: '.35rem' }} />
              <FormattedMessage id="inviteReminders.tab.template" defaultMessage="Reminder email template" />
            </CardTitle>
            <p style={{ margin: '.35rem 0 0', fontSize: '.82rem', color: '#64748b' }}>
              <FormattedMessage id="inviteReminders.template.intro"
                defaultMessage="One template for all events and speakers. Write the text as HTML; anything in double curly braces is filled in for each speaker when the email goes out. Click a placeholder below to insert it where your cursor is." />
            </p>
          </div>
          <Button type="button" onClick={() => setOpen(true)}>
            <FormattedMessage id="inviteReminders.template.editTitle" defaultMessage="Edit the reminder" />
          </Button>
        </div>
      </Card>
    );
  }

  if (loadError) {
    return <Card><Message error>{loadError}</Message></Card>;
  }
  if (!saved) {
    return <Card><Empty><FormattedMessage id="inviteReminders.loading" defaultMessage="Loading…" /></Empty></Card>;
  }

  const dirty = subject !== saved.subject || html !== saved.html;
  const problems = preview?.of === contentKey ? (preview.templateProblems || []) : null;
  const previewCurrent = preview?.of === contentKey && !previewing;
  // With nobody waiting there is nothing to preview against, so a dirty template is saveable; with a
  // person to render it for, the save waits for a clean preview of exactly the text being saved.
  const canSave = dirty && !saving && (!sampleFor || (previewCurrent && problems && problems.length === 0));

  const insert = (key) => {
    const token = `{{${key}}}`;
    const target = lastFocused.current === 'subject' ? subjectRef.current : htmlRef.current;
    const value = lastFocused.current === 'subject' ? subject : html;
    const set = lastFocused.current === 'subject' ? setSubject : setHtml;
    const start = target?.selectionStart ?? value.length;
    const end = target?.selectionEnd ?? value.length;
    set(value.slice(0, start) + token + value.slice(end));
    requestAnimationFrame(() => {
      if (!target) return;
      target.focus();
      target.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      apply(await Api.saveReminderTemplate(subject, html));
      setMessage({ text: t('inviteReminders.template.saved', 'Template saved. Every reminder from now on – automatic or manual – uses it.') });
    } catch (err) {
      setMessage({ error: true, text: backendMessage(err, t('inviteReminders.template.saveFailed', 'Could not save the template.')) });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setConfirmReset(false);
    setMessage(null);
    try {
      apply(await Api.resetReminderTemplate());
      setMessage({ text: t('inviteReminders.template.resetDone', 'The built-in default template is in use again.') });
    } catch (err) {
      setMessage({ error: true, text: backendMessage(err, t('inviteReminders.template.saveFailed', 'Could not save the template.')) });
    }
  };

  const status = saved.isDefault
    ? <FormattedMessage id="inviteReminders.template.isDefault" defaultMessage="Using the built-in default template." />
    : <FormattedMessage id="inviteReminders.template.savedBy" defaultMessage="Last saved {date}{by, select, none {} other { by {by}}}."
        values={{ date: intl.formatDate(new Date(saved.updatedAt), { dateStyle: 'medium', timeStyle: 'short' }), by: saved.updatedBy || 'none' }} />;

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <CardTitle style={{ margin: 0 }}>
          <Mail size={17} style={{ verticalAlign: '-3px', marginRight: '.35rem' }} />
          <FormattedMessage id="inviteReminders.tab.template" defaultMessage="Reminder email template" />
        </CardTitle>
        <Button type="button" secondary onClick={() => setOpen(false)}>
          <FormattedMessage id="programAdmin.reminderTemplate.close" defaultMessage="Hide" />
        </Button>
      </div>
      {message && <Message error={message.error} style={{ marginTop: '.75rem' }}>{message.text}</Message>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 30rem), 1fr))', gap: '1rem', alignItems: 'start', marginTop: '1rem' }}>
        {/* ----- editor ----- */}
        <div>
          <p style={{ margin: '0 0 1rem', fontSize: '.8rem', color: '#64748b' }}>
            <FormattedMessage id="inviteReminders.template.intro"
              defaultMessage="One template for all events and speakers. Write the text as HTML; anything in double curly braces is filled in for each speaker when the email goes out. Click a placeholder below to insert it where your cursor is." />
          </p>
          <label style={{ display: 'block', fontSize: '.82rem', fontWeight: 600, color: '#334155', marginBottom: '.35rem' }}>
            <FormattedMessage id="inviteReminders.template.subject" defaultMessage="Subject" />
          </label>
          <PlaceholderEditor ref={subjectRef} singleLine value={subject} onChange={setSubject} kindOf={kindOf}
            onFocus={() => { lastFocused.current = 'subject'; }} ariaLabel={t('inviteReminders.template.subject', 'Subject')} />
          <label style={{ display: 'block', fontSize: '.82rem', fontWeight: 600, color: '#334155', margin: '1rem 0 .35rem' }}>
            <FormattedMessage id="inviteReminders.template.body" defaultMessage="Email (HTML)" />
          </label>
          <PlaceholderEditor ref={htmlRef} value={html} onChange={setHtml} kindOf={kindOf} rows={22}
            onFocus={() => { lastFocused.current = 'html'; }} ariaLabel={t('inviteReminders.template.body', 'Email (HTML)')} />
          <div style={{ marginTop: '.5rem' }}><Legend /></div>

          <div style={{ marginTop: '1.25rem' }}>
            <div style={{ fontSize: '.82rem', fontWeight: 700, color: '#334155', marginBottom: '.5rem' }}>
              <FormattedMessage id="inviteReminders.template.placeholders" defaultMessage="Placeholders" />
            </div>
            {Object.entries(groups).map(([group, items]) => (
              <div key={group} style={{ marginBottom: '.8rem' }}>
                <div style={{ fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b', fontWeight: 700, marginBottom: '.35rem' }}>
                  {t(`inviteReminders.group.${group}`, group)}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                  {items.map((p) => (
                    <button key={p.key} type="button" onClick={() => insert(p.key)}
                      title={t('inviteReminders.template.insert', 'Insert {token}', { token: `{{${p.key}}}` })}
                      style={{ display: 'flex', gap: '.6rem', alignItems: 'baseline', textAlign: 'left', border: '1px solid #e2e8f0', background: '#fff',
                        padding: '.4rem .55rem', cursor: 'pointer', font: 'inherit' }}>
                      <code style={{ flex: 'none', fontSize: '.75rem', padding: '0 .3rem', ...KIND_STYLE[p.kind] }}>{`{{${p.key}}}`}</code>
                      <span style={{ fontSize: '.78rem', color: '#334155', lineHeight: 1.4 }}>
                        <strong>{phText(intl, p, 'label')}</strong> — {phText(intl, p, 'description')}
                        <span style={{ display: 'block', color: '#94a3b8', fontSize: '.72rem' }}>
                          <FormattedMessage id="inviteReminders.template.source" defaultMessage="Comes from: {source}" values={{ source: phText(intl, p, 'source') }} />
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ----- preview ----- */}
        <div style={{ position: 'sticky', top: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.6rem', flexWrap: 'wrap', marginBottom: '.6rem' }}>
            <CardTitle style={{ margin: 0 }}><Eye size={17} style={{ verticalAlign: '-3px', marginRight: '.35rem' }} />
              <FormattedMessage id="inviteReminders.preview.title" defaultMessage="Preview of the reminder" />
            </CardTitle>
            {previewing && <span style={{ fontSize: '.75rem', color: '#64748b' }}><FormattedMessage id="inviteReminders.preview.updating" defaultMessage="Updating…" /></span>}
          </div>
          {waiting.length === 0 ? (
            <Message warn style={{ marginBottom: '.6rem' }}>
              <FormattedMessage id="programAdmin.reminderTemplate.noSample"
                defaultMessage="Nobody at this event is waiting for an answer, so there is no one to fill the preview in for. The template is the same for every event and can still be edited and saved." />
            </Message>
          ) : (
            <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '.6rem', fontSize: '.8rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem', flex: '1 1 16rem', fontWeight: 600, color: '#334155' }}>
                <FormattedMessage id="inviteReminders.preview.as" defaultMessage="Fill in for" />
                <Select value={sampleFor} onChange={(e) => setSampleFor(e.target.value)} style={{ flex: 1, minWidth: 0, padding: '.4rem .5rem' }}>
                  {waiting.map((p) => <option key={p.personId} value={p.personId}>{p.personName || `#${p.personId}`}</option>)}
                </Select>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '.35rem', color: '#334155' }}>
                <input type="checkbox" checked={highlight} onChange={(e) => setHighlight(e.target.checked)} />
                <FormattedMessage id="inviteReminders.preview.highlight" defaultMessage="Mark filled-in parts" />
              </label>
            </div>
          )}
          {previewError
            ? <Message error>{previewError}</Message>
            : sampleFor && (
              <>
                <div style={{ fontSize: '.82rem', color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderBottom: 0, padding: '.45rem .6rem' }}>
                  <strong><FormattedMessage id="speakerMgmt.preview.subject" defaultMessage="Subject" />:</strong> {preview?.subject || '…'}
                </div>
                <iframe title={t('inviteReminders.preview.title', 'Preview of the reminder')} sandbox="" srcDoc={preview ? `<base target="_blank">${preview.html}` : ''}
                  style={{ width: '100%', height: '60vh', border: '1px solid #e2e8f0', background: '#fff', opacity: previewing ? .6 : 1, transition: 'opacity .15s' }} />
              </>
            )}
        </div>
      </div>

      {/* ----- save bar ----- */}
      <div style={{ marginTop: '1rem' }}>
        {problems && problems.length > 0 && (
          <Message error style={{ marginBottom: '.75rem' }}>
            <strong><FormattedMessage id="inviteReminders.template.problems" defaultMessage="This can't be saved yet:" /></strong>
            <ul style={{ margin: '.3rem 0 0', paddingLeft: '1.1rem' }}>{problems.map((p) => <li key={p}>{p}</li>)}</ul>
          </Message>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '.82rem', color: dirty ? '#92400e' : '#64748b', fontWeight: dirty ? 700 : 400 }}>
            {dirty ? <FormattedMessage id="inviteReminders.template.unsaved" defaultMessage="Unsaved changes – check the preview, then save." /> : status}
          </span>
          <Actions style={{ margin: 0 }}>
            {dirty && <Button secondary onClick={() => apply(saved)}><Undo2 size={15} /><FormattedMessage id="inviteReminders.template.discard" defaultMessage="Discard changes" /></Button>}
            {!saved.isDefault && <Button secondary onClick={() => setConfirmReset(true)}><RotateCcw size={15} /><FormattedMessage id="inviteReminders.template.reset" defaultMessage="Reset to default" /></Button>}
            <Button disabled={!canSave} onClick={save}
              title={sampleFor && dirty && !previewCurrent ? t('inviteReminders.template.waitPreview', 'Wait for the preview to show your latest changes') : undefined}>
              <Save size={15} />{saving
                ? <FormattedMessage id="inviteReminders.template.saving" defaultMessage="Saving…" />
                : <FormattedMessage id="inviteReminders.template.save" defaultMessage="Save template" />}
            </Button>
          </Actions>
        </div>
        {confirmReset && (
          <Message warn style={{ marginTop: '.75rem', marginBottom: 0 }}>
            <FormattedMessage id="inviteReminders.template.resetConfirm" defaultMessage="Replace the saved template with the built-in default? Your saved version is deleted. (To compare first, load the default into the editor – nothing is saved until you click Save.)" />
            <Actions style={{ marginTop: '.5rem' }}>
              <Button secondary onClick={() => setConfirmReset(false)}><FormattedMessage id="speakerMgmt.confirm.cancel" defaultMessage="Cancel" /></Button>
              <Button secondary onClick={() => { setConfirmReset(false); setSubject(saved.defaultSubject); setHtml(saved.defaultHtml); }}>
                <FormattedMessage id="inviteReminders.template.loadDefault" defaultMessage="Load default into editor" />
              </Button>
              <Button danger onClick={reset}><RotateCcw size={15} /><FormattedMessage id="inviteReminders.template.resetNow" defaultMessage="Reset now" /></Button>
            </Actions>
          </Message>
        )}
      </div>
    </Card>
  );
};

export default ReminderTemplateCard;
